import {
  AdjustmentsHorizontalIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  InboxIcon,
  ListBulletIcon,
  MegaphoneIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import {
  buildCrudPermissions,
  createLazyPlaceholderPage,
} from "../shared/featureFactories";

import { CONTACTS_PERMISSIONS } from "./contacts/permissions";
import { INBOX_PERMISSIONS } from "./inbox/permissions";
import { WHATSAPP_SETTINGS_PERMISSIONS } from "./whatsapp/permissions";

const configs = [
  {
    id: "announcements",
    label: "Announcements",
    path: "/announcements",
    icon: MegaphoneIcon,
    order: 1,
    permissions: buildCrudPermissions("Communication", "Announcements"),
  },
  {
    id: "sms-messaging",
    label: "SMS Messaging",
    path: "/sms-messaging",
    icon: ChatBubbleLeftRightIcon,
    order: 2,
    permissions: buildCrudPermissions("Communication", "SMSMessaging", {
      create: "Communication.SMSMessaging.send",
      edit: "Communication.SMSMessaging.bulkSend",
      delete: "Communication.SMSMessaging.bulkSend",
    }),
  },
  {
    id: "email-communication",
    label: "Email Communication",
    path: "/email-communication",
    icon: EnvelopeIcon,
    order: 3,
    permissions: buildCrudPermissions("Communication", "EmailCommunication", {
      create: "Communication.EmailCommunication.send",
      edit: "Communication.EmailCommunication.bulkSend",
      delete: "Communication.EmailCommunication.bulkSend",
    }),
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    path: "/whatsapp",
    icon: DevicePhoneMobileIcon,
    order: 4,
    permissions: buildCrudPermissions("Communication", "WhatsAppSettings", {
      view: WHATSAPP_SETTINGS_PERMISSIONS.read,
      create: WHATSAPP_SETTINGS_PERMISSIONS.manage,
      edit: WHATSAPP_SETTINGS_PERMISSIONS.manage,
      delete: WHATSAPP_SETTINGS_PERMISSIONS.manage,
    }),
  },
  {
    id: "push-notifications",
    label: "Send Notification",
    path: "/push-notifications",
    icon: BellAlertIcon,
    order: 5,
    permissions: buildCrudPermissions("Communication", "PushNotifications", {
      create: "Communication.PushNotifications.send",
      edit: "Communication.PushNotifications.send",
      delete: "Communication.PushNotifications.send",
    }),
  },
  {
    id: "scheduled-notifications",
    label: "Scheduled Notifications",
    path: "/scheduled-notifications",
    icon: ClockIcon,
    order: 6,
    permissions: buildCrudPermissions("Communication", "PushNotifications", {
      create: "Communication.PushNotifications.send",
      edit: "Communication.PushNotifications.send",
      delete: "Communication.PushNotifications.send",
    }),
  },
  {
    id: "notification-history",
    label: "Notification History",
    path: "/notification-history",
    icon: ListBulletIcon,
    order: 7,
    permissions: buildCrudPermissions("Communication", "PushNotifications", {
      create: "Communication.PushNotifications.send",
      edit: "Communication.PushNotifications.send",
      delete: "Communication.PushNotifications.send",
    }),
  },
  {
    id: "communication-templates",
    label: "Templates",
    path: "/communication-templates",
    icon: DocumentTextIcon,
    order: 8,
    permissions: buildCrudPermissions("Communication", "Templates", {
      view: "Communication.Templates.read",
      create: "Communication.Templates.manage",
      edit: "Communication.Templates.manage",
      delete: "Communication.Templates.manage",
    }),
  },
  {
    id: "notification-settings",
    label: "Notification Settings",
    path: "/notification-settings",
    icon: AdjustmentsHorizontalIcon,
    order: 9,
    permissions: buildCrudPermissions("Communication", "Settings", {
      view: "Communication.Settings.read",
      create: "Communication.Settings.manage",
      edit: "Communication.Settings.manage",
      delete: "Communication.Settings.manage",
    }),
  },
  {
    id: "communication-contacts",
    label: "Parent Contacts",
    path: "/communication-contacts",
    icon: UserGroupIcon,
    order: 10,
    permissions: buildCrudPermissions("Communication", "Contacts", {
      view: CONTACTS_PERMISSIONS.read,
      create: CONTACTS_PERMISSIONS.manage,
      edit: CONTACTS_PERMISSIONS.manage,
      delete: CONTACTS_PERMISSIONS.manage,
    }),
  },
  {
    id: "inbox",
    label: "Inbox",
    path: "/inbox",
    icon: InboxIcon,
    order: 11,
    // `manage` sees/assigns/resolves everything; `reply` sees/replies only to conversations
    // assigned to the caller — either one is enough to open the page (see InboxAccess, phase 4A).
    permissions: {
      ...buildCrudPermissions("Communication", "Inbox", {
        view: INBOX_PERMISSIONS.manage,
        create: INBOX_PERMISSIONS.reply,
        edit: INBOX_PERMISSIONS.manage,
        delete: INBOX_PERMISSIONS.manage,
      }),
      viewAny: [INBOX_PERMISSIONS.manage, INBOX_PERMISSIONS.reply],
    },
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  path: string;
  icon: FeatureMetadata["navigation"]["icon"];
  order: number;
  permissions: FeatureMetadata["permissions"];
}>;

export const COMMUNICATION_FEATURES: readonly FeatureMetadata[] = configs.map(
  ({ id, label, path, icon, order, permissions }) => ({
    id,
    moduleName: "communication",
    navigation: { label, path, icon, order },
    guardianNavigation:
      id === "announcements"
        ? {
            order: 7,
          }
        : undefined,
    permissions,
    route: {
      path,
      element:
        id === "announcements"
          ? lazy(() => import("./announcements/pages/AnnouncementsPage"))
          : id === "push-notifications"
            ? lazy(() => import("./pushNotifications/pages/PushNotifications"))
            : id === "scheduled-notifications"
              ? lazy(
                  () =>
                    import("./scheduledNotifications/pages/ScheduledNotificationsPage"),
                )
              : id === "notification-history"
                ? lazy(
                    () => import("./notificationHistory/pages/NotificationHistoryPage"),
                  )
                : id === "communication-templates"
                  ? lazy(() => import("./templates/pages/CommunicationTemplatesPage"))
                  : id === "notification-settings"
                    ? lazy(
                        () =>
                          import("./notificationSettings/pages/NotificationSettingsPage"),
                      )
                    : id === "whatsapp"
                      ? lazy(() => import("./whatsapp/pages/WhatsAppSettingsPage"))
                      : id === "communication-contacts"
                        ? lazy(() => import("./contacts/pages/ContactsPage"))
                        : id === "inbox"
                          ? lazy(() => import("./inbox/pages/InboxPage"))
                          : createLazyPlaceholderPage(label),
    },
    readiness: "live",
  }),
);
